import type { ApplicationClassification } from '@/lib/api/types';
import type { WorkspaceApplication } from './workspace-applications';

export type ShellApplicationClassification = ApplicationClassification | 'OTHER';
export type ShellApplicationClassificationFilter = 'ALL' | ShellApplicationClassification;

export interface ClassifiedWorkspaceApplication extends WorkspaceApplication {
  readonly classificationKey: ShellApplicationClassification;
}

export interface ClassificationOption {
  readonly key: ShellApplicationClassificationFilter;
  readonly label: string;
  readonly count: number;
}

export interface ClassificationFilterModel {
  readonly accessibleApplications: readonly ClassifiedWorkspaceApplication[];
  readonly filteredApplications: readonly ClassifiedWorkspaceApplication[];
  readonly visibleClassifications: readonly ClassificationOption[];
  readonly options: readonly ClassificationOption[];
  readonly shouldShowClassificationBar: boolean;
  readonly selectedClassification: ShellApplicationClassificationFilter;
}

export const CLASSIFICATION_LABELS: Record<ShellApplicationClassification, string> = {
  COMMUNITY: 'COMMUNITY',
  FLOW: 'FLOW',
  INTERNAL: 'INTERNAL',
  INVEST: 'INVEST',
  OTHER: 'OTHER',
  PUBLIC: 'PUBLIC',
  SHARED: 'SHARED',
};

export function normalizeApplicationClassification(
  classification?: ApplicationClassification | string | null,
): ShellApplicationClassification {
  if (!classification) {
    return 'OTHER';
  }

  const normalized = classification.toUpperCase();
  if (normalized in CLASSIFICATION_LABELS) {
    return normalized as ShellApplicationClassification;
  }

  return 'OTHER';
}

export function classifyWorkspaceApplications(
  applications: readonly WorkspaceApplication[],
): ClassifiedWorkspaceApplication[] {
  return applications.map((application) => ({
    ...application,
    classificationKey: normalizeApplicationClassification(application.classification),
  }));
}

export function buildClassificationFilterModel(
  applications: readonly WorkspaceApplication[],
  selectedClassification: ShellApplicationClassificationFilter,
): ClassificationFilterModel {
  const accessibleApplications = classifyWorkspaceApplications(applications);
  const counts = new Map<ShellApplicationClassification, number>();

  for (const application of accessibleApplications) {
    counts.set(application.classificationKey, (counts.get(application.classificationKey) ?? 0) + 1);
  }

  const visibleClassifications = [...counts.entries()]
    .sort(([left], [right]) => CLASSIFICATION_LABELS[left].localeCompare(CLASSIFICATION_LABELS[right]))
    .map(([key, count]) => ({
      key,
      label: CLASSIFICATION_LABELS[key],
      count,
    }));

  const shouldShowClassificationBar =
    accessibleApplications.length > 1 && visibleClassifications.length > 1;

  const effectiveSelectedClassification =
    shouldShowClassificationBar &&
    selectedClassification !== 'ALL' &&
    visibleClassifications.some((option) => option.key === selectedClassification)
      ? selectedClassification
      : 'ALL';

  const filteredApplications =
    !shouldShowClassificationBar || effectiveSelectedClassification === 'ALL'
      ? accessibleApplications
      : accessibleApplications.filter(
          (application) => application.classificationKey === effectiveSelectedClassification,
        );

  const options = shouldShowClassificationBar
    ? [
        {
          key: 'ALL' as const,
          label: 'All',
          count: accessibleApplications.length,
        },
        ...visibleClassifications,
      ]
    : [];

  return {
    accessibleApplications,
    filteredApplications,
    visibleClassifications,
    options,
    shouldShowClassificationBar,
    selectedClassification: effectiveSelectedClassification,
  };
}
